// socket.service.ts
import { Injectable, NgZone, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export type PackageSocketEvent =
  | 'PackageInLocker'
  | 'PackageConsumerPickup'
  | 'PackageExpired'
  | 'PackageReminderInLocker'
  | 'Notification';

export type SocketSystemEvent =
  | 'auth_success'
  | 'auth_error'
  | 'pong'
  | 'error'
  | 'connection_open'
  | 'connection_closed'
  | 'connection_error'
  | 'connection_lost'
  | 'reconnect_attempt'
  | 'reconnect';

export type SocketListenerEvent =
  | PackageSocketEvent
  | SocketSystemEvent
  | 'notification_changed';

export interface SocketMessage<T = unknown> {
  event?: PackageSocketEvent | SocketSystemEvent;
  action?: string;
  data?: T;
  message?: string;
  timestamp?: string;
}

export interface NotificationChangedPayload<T = unknown> {
  event: PackageSocketEvent;
  data?: T;
}

type ListenerCallback<T = unknown> = (data?: T) => void;

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  private ws: WebSocket | null = null;

  private readonly baseUrl = environment.url_server_socket;

  /**
   * Mantén "suscribe" si el backend tiene exactamente este endpoint.
   */
  private readonly path = '/socket/suscribe';

  private listeners: Map<string, ListenerCallback[]> = new Map();

  private token: string | null = null;
  private lang = 'es';

  private isConnecting = false;
  private manualDisconnect = false;

  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 5;

  private pingInterval: ReturnType<typeof setInterval> | null = null;

  private zone = inject(NgZone);

  // ======================================================
  // Conexión
  // ======================================================

  public connect(token: string | null, lang: string = 'es'): void {
    if (!token) {
      console.error('❌ No se puede conectar WebSocket sin token');
      this.disconnect();
      return;
    }

    if (
      this.isConnecting ||
      this.ws?.readyState === WebSocket.OPEN
    ) {
      return;
    }

    this.token = token;
    this.lang = lang;
    this.isConnecting = true;
    this.manualDisconnect = false;

    const socketUrl = this.buildSocketUrl(token, lang);

    console.log('🔌 Conectando WebSocket:', this.getSafeSocketUrl(socketUrl));

    this.ws = new WebSocket(socketUrl);

    this.ws.onopen = () => {
      this.zone.run(() => {
        console.log('✅ WebSocket conectado');

        this.isConnecting = false;
        this.reconnectAttempts = 0;

        this.emitLocal('connection_open', {
          state: this.getConnectionState(),
        });

        /**
         * Se manda auth también por mensaje, por si el backend autentica
         * después de abrir la conexión.
         */
        this.send({
          action: 'auth',
          token,
          lang,
        });

        this.startPing();
      });
    };

    this.ws.onmessage = (event: MessageEvent) => {
      this.zone.run(() => {
        this.handleRawMessage(event.data);
      });
    };

    this.ws.onerror = (event: Event) => {
      this.zone.run(() => {
        console.error('❌ Error en WebSocket:', {
          readyState: this.ws?.readyState,
          event,
        });

        this.isConnecting = false;

        this.emitLocal('connection_error', {
          readyState: this.ws?.readyState,
        });
      });
    };

    this.ws.onclose = (event: CloseEvent) => {
      this.zone.run(() => {
        console.warn('🔌 WebSocket cerrado:', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          manualDisconnect: this.manualDisconnect,
        });

        this.isConnecting = false;
        this.stopPing();

        this.emitLocal('connection_closed', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          manualDisconnect: this.manualDisconnect,
        });

        if (this.manualDisconnect) {
          return;
        }

        this.handleReconnect();
      });
    };
  }

  private buildSocketUrl(token: string, lang: string): string {
    if (!this.baseUrl) {
      throw new Error('environment.url_server_socket no está definida');
    }

    const normalizedBaseUrl = this.normalizeWebSocketUrl(this.baseUrl);
    const url = new URL(normalizedBaseUrl);

    /**
     * Si environment.url_server_socket viene sin path,
     * agregamos /socket/suscribe.
     */
    if (!url.pathname || url.pathname === '/') {
      url.pathname = this.path;
    }

    url.searchParams.set('token', token);
    url.searchParams.set('lang', lang);

    return url.toString();
  }

  private normalizeWebSocketUrl(rawUrl: string): string {
    const url = new URL(rawUrl);

    if (url.protocol === 'https:') {
      url.protocol = 'wss:';
    }

    if (url.protocol === 'http:') {
      url.protocol = 'ws:';
    }

    const isLocalhost =
      url.hostname === 'localhost' ||
      url.hostname === '127.0.0.1' ||
      url.hostname.startsWith('192.168.') ||
      url.hostname.startsWith('10.');

    /**
     * Para dominios externos, evitamos ws:// inseguro.
     */
    if (!isLocalhost && url.protocol === 'ws:') {
      url.protocol = 'wss:';
    }

    return url.toString();
  }

  private getSafeSocketUrl(socketUrl: string): string {
    try {
      const url = new URL(socketUrl);

      if (url.searchParams.has('token')) {
        url.searchParams.set('token', '***');
      }

      return url.toString();
    } catch {
      return socketUrl;
    }
  }

  // ======================================================
  // Mensajes recibidos
  // ======================================================

  private handleRawMessage(rawData: unknown): void {
    try {
      const message: SocketMessage = this.parseMessage(rawData);
      this.handleMessage(message);
    } catch (error) {
      console.error('❌ Error procesando mensaje WebSocket:', {
        error,
        rawData,
      });
    }
  }

  private parseMessage(rawData: unknown): SocketMessage {
    if (typeof rawData === 'string') {
      return JSON.parse(rawData) as SocketMessage;
    }

    return rawData as SocketMessage;
  }

  private handleMessage(message: SocketMessage): void {
    if (!message) {
      return;
    }

    if (message.event === 'auth_success') {
      console.log('✅ Autenticación WebSocket exitosa');
      this.emitLocal('auth_success', message);
      return;
    }

    if (message.event === 'auth_error') {
      console.error('❌ Error de autenticación WebSocket:', message.message);

      this.emitLocal('auth_error', message);

      /**
       * Si falla auth, no conviene reconectar con el mismo token.
       */
      this.disconnect();

      return;
    }

    if (message.event === 'pong') {
      this.emitLocal('pong', message);
      return;
    }

    if (message.event === 'error') {
      console.error('❌ Error del servidor WebSocket:', message.message);
      this.emitLocal('error', message);
      return;
    }

    const packageEvents: PackageSocketEvent[] = [
      'PackageInLocker',
      'PackageConsumerPickup',
      'PackageExpired',
      'PackageReminderInLocker',
      'Notification',
    ];

    if (message.event && packageEvents.includes(message.event as PackageSocketEvent)) {
      this.handlePackageEvent(
        message.event as PackageSocketEvent,
        message.data
      );

      return;
    }

    console.warn('⚠ Evento WebSocket no reconocido:', message);
  }

  private handlePackageEvent(event: PackageSocketEvent, data?: unknown): void {
    console.log(`📨 Evento recibido: ${event}`, data);

    this.emitLocal(event, data);

    const payload: NotificationChangedPayload = {
      event,
      data,
    };

    /**
     * Evento agrupador para refrescar tablas, contadores o campana.
     */
    this.emitLocal('notification_changed', payload);
  }

  // ======================================================
  // API tipo realtimeService
  // ======================================================

  public on<T = unknown>(
    event: SocketListenerEvent | string,
    callback: ListenerCallback<T>
  ): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }

    this.listeners.get(event)?.push(callback as ListenerCallback);
  }

  public off<T = unknown>(
    event: SocketListenerEvent | string,
    callback: ListenerCallback<T>
  ): void {
    if (!this.listeners.has(event)) {
      return;
    }

    const callbacks = this.listeners.get(event) || [];
    const filteredCallbacks = callbacks.filter((fn) => fn !== callback);

    this.listeners.set(event, filteredCallbacks);
  }

  private emitLocal(event: string, data?: unknown): void {
    if (!this.listeners.has(event)) {
      return;
    }

    this.listeners.get(event)?.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error(`❌ Error en listener de ${event}:`, error);
      }
    });
  }

  // ======================================================
  // API Observable para Angular
  // ======================================================

  public listen<T = unknown>(eventName: SocketListenerEvent | string): Observable<T> {
    return new Observable<T>((observer) => {
      const callback: ListenerCallback<T> = (data?: T) => {
        observer.next(data as T);
      };

      this.on<T>(eventName, callback);

      return () => {
        this.off<T>(eventName, callback);
      };
    });
  }

  public onPackageInLocker(): Observable<unknown> {
    return this.listen('PackageInLocker');
  }

  public onPackageConsumerPickup(): Observable<unknown> {
    return this.listen('PackageConsumerPickup');
  }

  public onPackageExpired(): Observable<unknown> {
    return this.listen('PackageExpired');
  }

  public onPackageReminderInLocker(): Observable<unknown> {
    return this.listen('PackageReminderInLocker');
  }

  public onGeneralNotification(): Observable<unknown> {
    return this.listen('Notification');
  }

  public onNotificationChanged(): Observable<NotificationChangedPayload> {
    return this.listen<NotificationChangedPayload>('notification_changed');
  }

  // ======================================================
  // Enviar mensajes
  // ======================================================

  public send(payload: Record<string, unknown>): void {
    if (this.ws?.readyState !== WebSocket.OPEN) {
      console.warn('SocketService.send: WebSocket no conectado', payload);
      return;
    }

    this.ws.send(JSON.stringify(payload));
  }

  public emit(event: string, payload?: unknown): void {
    this.send({
      event,
      data: payload,
    });
  }

  public ping(): void {
    this.send({
      action: 'ping',
      timestamp: new Date().toISOString(),
    });
  }

  private startPing(): void {
    this.stopPing();

    this.pingInterval = setInterval(() => {
      this.ping();
    }, 30000);
  }

  private stopPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  // ======================================================
  // Reconexión
  // ======================================================

  private handleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ Máximos intentos de reconexión alcanzados');

      this.emitLocal('connection_lost', {
        reason: 'max_attempts',
      });

      return;
    }

    const delay = Math.min(
      1000 * Math.pow(2, this.reconnectAttempts),
      30000
    );

    this.reconnectAttempts++;

    console.log(
      `🔄 Reconectando en ${delay}ms ` +
      `(intento ${this.reconnectAttempts}/${this.maxReconnectAttempts})`
    );

    this.emitLocal('reconnect_attempt', {
      attempt: this.reconnectAttempts,
      maxAttempts: this.maxReconnectAttempts,
      delay,
    });

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectTimer = setTimeout(() => {
      if (this.token && !this.manualDisconnect) {
        this.connect(this.token, this.lang);

        this.emitLocal('reconnect', {
          attempt: this.reconnectAttempts,
        });
      }
    }, delay);
  }

  public reconnect(): void {
    if (!this.token) {
      console.warn('SocketService.reconnect: no hay token guardado');
      return;
    }

    this.manualDisconnect = false;

    if (this.ws) {
      try {
        this.ws.close();
      } catch {}
    }

    this.ws = null;
    this.isConnecting = false;

    this.connect(this.token, this.lang);
  }

  // ======================================================
  // Desconexión
  // ======================================================

  public disconnect(): void {
    this.manualDisconnect = true;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.stopPing();

    if (this.ws) {
      try {
        this.ws.close(1000, 'Manual disconnect');
      } catch {}

      this.ws = null;
    }

    this.isConnecting = false;
    this.token = null;

    console.log('🔌 WebSocket desconectado manualmente');
  }

  // ======================================================
  // Estado
  // ======================================================

  public isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  public getConnectionState(): string {
    if (!this.ws) {
      return 'disconnected';
    }

    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';

      case WebSocket.OPEN:
        return 'open';

      case WebSocket.CLOSING:
        return 'closing';

      case WebSocket.CLOSED:
        return 'closed';

      default:
        return 'unknown';
    }
  }
}